<ng-template let-extraClass="extraClass" #topNav>
  @if (!isRightSideContent) {
    <tidy-top-nav
      [class]="extraClass"
      [title]="'Rent Collection Settings'"
      [canGoBack]="!windowService.isDesktopRes" />
  }
</ng-template>

<ng-container *ngTemplateOutlet="topNav;context:{ extraClass: 'mobile' }" />

<ng-template #content>
  <tidy-wrapper [class]="isRightSideContent ? 'right-side-scroll-enabled' : ''">

    @if (errorMessage) {
      <tidy-error-message [text]="errorMessage" />
    }

    @if (!loaded) {
      @for (i of [1,2,3,4]; track i) {
        <div style="width: 100%; margin-bottom: 6px;">
          <ion-skeleton-text animated="true" style="height: 60px; border-radius: 6px" />
        </div>
      }
    }

    @if (loaded) {

      <!-- Payment methods -->
      <tidy-row><tidy-text [title]="'Accepted payment methods'" /></tidy-row>
      <tidy-row class="extra-bottom-padding">
        <tidy-text [helper]="'Choose what tenants and guests can pay with, and who covers each method’s processing fee.'" />
      </tidy-row>
      <div class="compact-settings-table">
        <tidy-table
          [dataSource]="methodRows"
          [displayedColumns]="methodHeaders"
          [columnslabels]="methodHeaders"
          [showColumnLines]="false"
          [enableVirtualScrolling]="false"
          [showPaginator]="false"
          (selectChange)="onMethodSelect($event)"
          [blockSorting]="true" />
      </div>

      <!-- Payout account -->
      <tidy-row class="extra-top-padding"><tidy-text [title]="'Payout account'" /></tidy-row>
      @if (!payout.connected) {
        <tidy-alert>
          <tidy-row>
            <tidy-text [body]="('TIDY collects rent on your behalf. Connect a ' | translate) + payout.provider + (' account so collected funds can be deposited to you.' | translate)" />
          </tidy-row>
          <tidy-row class="extra-top-padding">
            <tidy-button
              [text]="isConnecting ? 'Connecting…' : ('Connect ' + payout.provider + ' Payout Account')"
              [action]="connectPayoutAction"
              class="primary" />
          </tidy-row>
        </tidy-alert>
      }
      @if (payout.connected) {
        <tidy-card>
          <tidy-row [alignLastItemRight]="true" class="vertical-align-center">
            <tidy-text [body]="payout.provider + ' · ' + payout.account_name + '  ••' + payout.last4" [translate]="false" />
            <tidy-text [body]="'Connected'" [ngStyle]="{ color: 'var(--primary-button, #4295a3)' }" />
          </tidy-row>
          <tidy-divider />
          <tidy-row class="extra-top-padding"><tidy-text [helper]="'Payout schedule'" /></tidy-row>
          <tidy-row class="vertical-align-center">
            @for (opt of scheduleItems; track opt.value) {
              <button
                type="button"
                (click)="setSchedule(opt.value)"
                [ngStyle]="{
                  'padding': '6px 14px', 'margin-right': '8px', 'border-radius': '16px', 'cursor': 'pointer',
                  'border': '1px solid ' + (payout.schedule === opt.value ? '#1a8e9c' : '#e0e0e0'),
                  'background': payout.schedule === opt.value ? '#1a8e9c' : 'transparent',
                  'color': payout.schedule === opt.value ? '#ffffff' : '#6c6c6c', 'font-size': '13px'
                }">{{ opt.viewValue }}</button>
            }
          </tidy-row>
          <tidy-row class="extra-top-padding">
            <tidy-text [helper]="'Funds settle ~3 business days after capture, then pay out on this schedule.'" />
          </tidy-row>
        </tidy-card>
      }

      <!-- Payment reminders cadence -->
      <tidy-row class="extra-top-padding"><tidy-text [title]="'Payment reminders'" /></tidy-row>
      <tidy-row class="extra-bottom-padding">
        <tidy-text [helper]="'Sent automatically around the due date. Every reminder links the payer straight to their secure payment page.'" />
      </tidy-row>
      <form [formGroup]="rulesForm" novalidate action="#">
        @for (rule of reminders.rules; track rule.id) {
          <tidy-card>
            <tidy-row [alignLastItemRight]="true" class="vertical-align-center">
              <tidy-text [body]="rule.label" [translate]="false" />
              <tidy-toggle
                [toggled]="rule.enabled"
                (toggleChanged)="toggleRule(rule)" />
            </tidy-row>
            @if (rule.enabled) {
              <tidy-row class="extra-top-padding">
                <tidy-textarea
                  [formControlName]="'rule_' + rule.id"
                  [form]="rulesForm.controls['rule_' + rule.id]"
                  [icon]="'assets/svg/create-outline.svg'" />
              </tidy-row>
            }
          </tidy-card>
        }
      </form>

      <!-- Branding -->
      <tidy-row class="extra-top-padding"><tidy-text [title]="'Payment page branding'" /></tidy-row>
      <tidy-card>
        <tidy-row><tidy-text [helper]="'Accent color'" /></tidy-row>
        <tidy-row class="vertical-align-center">
          @for (color of accentSwatches; track color) {
            <button
              type="button"
              (click)="selectAccent(color)"
              [ngStyle]="{
                'width': '26px', 'height': '26px', 'border-radius': '50%', 'margin-right': '8px', 'cursor': 'pointer',
                'background': color,
                'border': accentColor === color ? '3px solid #1E2125' : '2px solid #ffffff',
                'box-shadow': '0 0 0 1px #e0e0e0'
              }"></button>
          }
        </tidy-row>
        <form [formGroup]="reminderForm" novalidate action="#">
          <tidy-input
            [label]="'Payment page heading'"
            formControlName="heading"
            [form]="reminderForm.controls.heading" />
          <tidy-textarea
            [label]="'Payment page subtext'"
            formControlName="message"
            [form]="reminderForm.controls.message"
            [icon]="'assets/svg/create-outline.svg'" />
        </form>
        <tidy-row>
          <tidy-text
            [actionBody]="'Upload logo'"
            (action)="uploadLogo()"
            class="link" />
        </tidy-row>
      </tidy-card>

      <!-- Live preview of the hosted pay page -->
      <tidy-row class="extra-top-padding"><tidy-text [title]="'Payment page preview'" /></tidy-row>
      <tidy-card>
        <div [ngStyle]="{ 'border-top': '4px solid ' + accentColor, 'padding-top': '12px' }">
          <tidy-row class="vertical-align-center">
            <div [ngStyle]="{ 'width': '28px', 'height': '28px', 'border-radius': '6px', 'background': accentColor, 'margin-right': '8px', 'display': 'flex', 'align-items': 'center', 'justify-content': 'center', 'color': '#fff', 'font-weight': '700', 'font-size': '14px' }">T</div>
            <tidy-text [body]="'Your Brand'" [translate]="false" />
          </tidy-row>
          <tidy-row class="extra-top-padding">
            <tidy-text [header]="previewHeading" [translate]="false" />
          </tidy-row>
          <tidy-row>
            <tidy-text [helper]="previewMessage" [translate]="false" />
          </tidy-row>
          <tidy-row class="extra-top-padding">
            <tidy-text [body]="'August rent · Oakwood Apartments Apt 3B'" [translate]="false" />
          </tidy-row>
          <tidy-row>
            <tidy-text [title]="'$1,850.00'" [translate]="false" />
          </tidy-row>
          <tidy-row class="extra-top-padding">
            <div [ngStyle]="{ 'display': 'inline-block', 'padding': '10px 18px', 'border-radius': '8px', 'background': accentColor, 'color': '#fff', 'font-weight': '600', 'font-size': '14px' }">Pay $1,850.00</div>
          </tidy-row>
        </div>
      </tidy-card>

      <tidy-row class="extra-top-padding">
        <tidy-button [text]="'Save Reminders'" [action]="saveRemindersAction" class="primary" />
      </tidy-row>
    }
  </tidy-wrapper>
</ng-template>

@if (!isRightSideContent) {
  <app-scroll-wrapper [templateContent]="content" />
}

@if (isRightSideContent) {
  <ion-content class="ion-content-right-column">
    <ng-container *ngTemplateOutlet="content" />
  </ion-content>
}
